import { useSelector } from '@xstate/react';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { UserContext } from '~/app';
import { MarketSegmentedControl } from '~/features/shared/market-content';
import { selectUnmaskViewerScope } from '~/features/shared/market-content/responseViewerScope';
import type { ListingSide } from '../b2bSideCopy';
import {
  parseRememberedSide,
  readRememberedSide,
  storeRememberedSide,
  type RememberedSide,
} from '../rememberedSide';
import { ListingSearchPane } from './ListingSearchPane';
import { SearchSideProvider } from './searchSide';

type MarketplaceTab = RememberedSide;

const TAB_OPTIONS: Array<{ label: string; value: MarketplaceTab; cyTag: string }> = [
  { label: 'Buying', value: 'buying', cyTag: 'b2b-search-side-buying' },
  { label: 'Selling', value: 'selling', cyTag: 'b2b-search-side-selling' },
];

// Buying lists OFFERING listings (labor available to hire); Selling lists SEEKING listings
// (employers who need labor), which is what this page showed before the tabs.
const SIDE_BY_TAB: Record<MarketplaceTab, ListingSide> = {
  buying: 'OFFERING',
  selling: 'SEEKING',
};

export const ListingSearchTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  const storedTab = useMemo(
    () => readRememberedSide('marketplace', viewerScopeKey),
    [viewerScopeKey]
  );
  // ?side= always wins, then the last tab used, then Selling (today's results) on a first visit.
  const tab = parseRememberedSide(searchParams.get('side')) ?? storedTab ?? 'selling';

  const selectTab = (next: MarketplaceTab) => {
    storeRememberedSide('marketplace', viewerScopeKey, next);
    const params = new URLSearchParams(searchParams);
    params.set('side', next);
    setSearchParams(params, { replace: true });
  };

  const side = SIDE_BY_TAB[tab];
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0">
        <MarketSegmentedControl
          className="w-full sm:w-auto"
          options={TAB_OPTIONS}
          value={tab}
          onChange={selectTab}
        />
      </div>
      <SearchSideProvider value={side}>
        <ListingSearchPane key={side} side={side} responsive />
      </SearchSideProvider>
    </div>
  );
};
